import React, { Fragment } from 'react'
import { Body } from 'components/Shell/Shell'
import shortid from 'shortid'
import CollapsibleTable from 'components/Table/CollapsibleTable'
import Toolbar from 'components/Table/Toolbar'
import { parseUrlQuery } from 'utilities'
import debounce from 'lodash/debounce'
import orderBy from 'lodash/orderBy'
import CaretDownIcon from '@tecuity/sos-icons/react/caret-down'
import { GET_LOBBYISTS_BY_SEARCH_AND_RANGE, GET_LOBBYIST_ENTITIES_BY_ID, BASE_URL } from 'requests'
import FloatingMenu from 'components/FloatingMenu/FloatingMenu'
import DownloadIcon from '@tecuity/sos-icons/react/download-box'

const CURRENT_YEAR = (new Date().getMonth() > 7) ? (new Date().getFullYear() + 1) : (new Date().getFullYear())

export default class List extends React.Component {
  state = {
    filter: '',
    expanded: false
  }
  setFilter = debounce(filter => {
    this.setState({ filter, expanded: filter.length > 1 })
  })
  componentDidMount = () => {
    this.forceScrollbar(true)
  }
  componentWillUnmount = () => {
    this.forceScrollbar(false)
  }
  forceScrollbar = force => {
    if (force) {
      document.body.style.overflowY = 'scroll'
    } else {
      document.body.style.overflowY = ''
    }
  }
  getFilteredRows = () => {
    const filter = this.state.filter.toLowerCase()
    // console.log(filter);
    return this.props.rows.filter(row => {
      return (
        this.rowIncludesFilter(row.CELLS, filter) || this.childIncludesFilter(row.CHILDREN, filter)
      )
    }, 100)
  }
  rowIncludesFilter = (cells, filter) => {
    const filterableIndecies = this.props.filterableIndecies
    let i = 0
    while (true) {
      if (i === filterableIndecies.length) {
        return false
      }
      if (cells[filterableIndecies[i]].toLowerCase().includes(filter)) {
        return true
      }
      i++
    }
  }
  childIncludesFilter = (rows, filter) => {
    let i = 0
    while (true) {
      if (i === rows.length) {
        return false
      }
      if (rows[i][0].toLowerCase().includes(filter)) {
        return true
      }
      i++
    }
  }
  toggleExpansion = () => {
    this.setState(state => ({ expanded: !state.expanded }))
  }
  render() {
    const { headers, rows, title, getChildren, onSearchBlur, tableKey, searching, underToolbar } = this.props
    return (
      <Body>
        <Toolbar
          filter={this.state.filter}
          onExpansionChange={this.toggleExpansion}
          title={title}
          onSearchBlur={onSearchBlur}
          searchPlaceholder="Search by Lobbyist or Entity"
          className="center-container"
          searching={searching}
          underToolbar={underToolbar}
        />
        <CollapsibleTable
          className="center-container"
          rows={this.state.filter !== '' ? this.getFilteredRows() : rows}
          expanded={this.state.expanded}
          getChildren={getChildren}
          headers={headers}
          key={tableKey || undefined}
          expandable
        />
      </Body>
    )
  }
}

export class LobbyistList extends React.Component {
  state = {
    rows: [],
    template: [],
    rawRows: [],
    headers: [],
    year: parseUrlQuery(this.props.location.search).year,
    search: '',
    yearMenuOpen: false,
    searching: false
  }
  componentDidMount = () => {
    const search = parseUrlQuery(this.props.location.search)
    this.getLobbyistsbyRange(search.year, this.state.search)
  }
  getLobbyistsbyRange = (year, search = '') => {
    if(search){this.setState({searching: true})}
    GET_LOBBYISTS_BY_SEARCH_AND_RANGE(year, search, lobbyists => {
      const rawRows = Object.values(lobbyists.rows).reduce((arr, val) => [...arr, val], [])
      this.setState({
        rows: orderBy(rawRows, ['SORT_INDEX']).map(l => ({
          render: lobbyists.template.map(t => {
            if (Array.isArray(l[t.id])) {
              return (
                <span className="stack">{l[t.id].map((x, i) => <span key={i}>{x}</span>)}</span>
              )
            } else {
              return l[t.id]
            }
          }),
          id: l.ID
        })),
        tableKey: shortid.generate(),
        rawRows: orderBy(rawRows, ['SORT_INDEX']),
        template: lobbyists.template,
        headers: lobbyists.template.map(t => t.label),
        searching: false
      })
    })
  }
  refetchBySearch = search => {
    if (search !== this.state.search) {
      this.setState({ search })
      this.getLobbyistsbyRange(this.state.year, search)
    }
  }
  getEntities = (row, index) =>
    new Promise((resolve, reject) => {
      GET_LOBBYIST_ENTITIES_BY_ID(
        this.state.rawRows[index].LOBBYIST_ID,
        this.state.year,
        ({ ENTITIES, EXPENDITURES }) => {
          if (ENTITIES.rows.length > 0) {
            resolve(
              <SubTable
                headers={ENTITIES.template.map(t => t.label)}
                rows={ENTITIES.rows.map(row =>
                  ENTITIES.template.map(t => (
                    <HistoryCell
                      from={row[t.id].CHANGED_FROM}
                      to={row[t.id].CHANGED_TO}
                      key={t.id}
                    />
                  ))
                )}
              />
            )
          } else {
            resolve(
              <div className="message">
                This lobbyist does not represent any entities in {this.state.year}
              </div>
            )
          }
        },
        err => {
          reject(err)
        }
      )
    })
  toggleYearMenu = () => {
    this.setState(state => ({yearMenuOpen: !state.yearMenuOpen}))
  }
  setYear = ({value: year}) => {
    this.props.history.replace({
      pathname: this.props.location.pathname,
      search: `?year=${year}`
    })
    this.setState({year})
    this.getLobbyistsbyRange(year, this.state.search)
  }
  render() {
    return (
      <List
        title={
          <Fragment>
            Registered Lobbyists:{' '}
            <span
              className="subtitle"
              ref={ref => (this.yearTitle = ref)}
              onClick={this.toggleYearMenu}
            >
              {this.state.year} - {parseInt(this.state.year, 10) + 1}
              <CaretDownIcon />
            </span>
            <FloatingMenu
              className="fm-icon-menu"
              isOpen={this.state.yearMenuOpen}
              alignTo={this.yearTitle}
              align="right"
              onRequestClose={this.toggleYearMenu}
              options={Array(5)
                .fill(0)
                .map((_, i) => ({
                  value: CURRENT_YEAR - 5 + i,
                  label: `${CURRENT_YEAR - 5 + i} - ${CURRENT_YEAR - 4 + i}`,
                  onClick: this.setYear
                }))}
            />
          </Fragment>
        }
        tableKey={this.state.tableKey}
        getChildren={this.getEntities}
        onSearchBlur={this.refetchBySearch}
        headers={this.state.headers}
        rows={this.state.rows}
        searching={this.state.searching}
        underToolbar={
          <div className="download-link">
            <a href={`${BASE_URL}/api/list/csv/${this.state.year}`} target="_blank" download>
              <DownloadIcon/> <span>Download as .csv</span>
            </a>
          </div>
        }
        // rows={testData}
      />
    )
  }
}

const SubTable = ({ rows, headers }) => (
  <table>
    {headers && (
      <thead>
        <tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
      </thead>
    )}
    <tbody>
      {rows.map((row, i) => <tr key={i}>{row.map((cell, k) => <td key={k}>{cell}</td>)}</tr>)}
    </tbody>
  </table>
)

const HistoryCell = ({ from, to }) => (
  <div className="history-cell">
    {from !== null && (
      <React.Fragment>
        <label>Changed From:</label>
        <span className="value">{from}</span>
      </React.Fragment>
    )}
    {from !== null ? (
      <React.Fragment>
        <label>Changed To:</label>
        <span className="value">{to}</span>
      </React.Fragment>
    ) : (
      to
    )}
  </div>
)



// WEBPACK FOOTER //
// ./src/components/Lists/List.js